import React, { useContext, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Button,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { LockOutlined as LockOutlinedIcon, MusicNote, Headphones } from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [multipleAccounts, setMultipleAccounts] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState(null);

  const { login, loading, error, setError, resendVerificationEmail } = useContext(AuthContext);
  const navigate = useNavigate();

  const { email, password } = formData;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear errors when user starts typing again
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError(null);
    setNeedsVerification(false);
    setMultipleAccounts(null);
    
    if (validateForm()) {
      try {
        const result = await login(email, password, selectedAccountType);
        
        // Check if multiple accounts exist and user needs to choose
        if (result.multipleAccounts) {
          setMultipleAccounts(result);
          return;
        }
        
        navigate('/dashboard');
      } catch (err) {
        // Check if this is an email verification error
        if (err.response?.data?.needsVerification) {
          setNeedsVerification(true);
          setVerificationEmail(err.response.data.email || email);
        }
      }
    }
  };

  const handleAccountSelection = async (accountType) => {
    setSelectedAccountType(accountType);
    
    try {
      await login(email, password, accountType);
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.needsVerification) {
        setNeedsVerification(true);
        setVerificationEmail(err.response.data.email || email);
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail(verificationEmail);
      setError('Verification email sent! Please check your inbox.');
    } catch (err) {
    }
  };

  return (
    <Grid container component="main" sx={{ height: '80vh' }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(https://source.unsplash.com/random?music,studio)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: t => t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Log in to GrooveGate
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          
          {multipleAccounts && (
            <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
              <Box>
                <Typography variant="body2" paragraph>
                  {multipleAccounts.message}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Please select which account you want to log in with:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  {multipleAccounts.accounts.map((account, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      startIcon={account.accountType === 'artist' ? <MusicNote /> : <Headphones />}
                      onClick={() => handleAccountSelection(account.accountType)}
                      disabled={loading}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      <Box sx={{ textAlign: 'left', flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {account.accountType === 'artist' ? 'Artist Account' : 'Curator Account'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.artistName}
                          {account.accountType === 'curator' && account.djName && account.djName !== account.artistName && ` (${account.djName})`}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Box>
              </Box>
            </Alert>
          )}
          
          {needsVerification && (
            <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
              <Box>
                <Typography variant="body2" paragraph>
                  Please verify your email address before logging in.
                </Typography>
                <Button
                  size="small"
                  onClick={handleResendVerification}
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  Resend Verification Email
                </Button>
              </Box>
            </Alert>
          )}
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={handleChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Log In'}
            </Button>
            <Grid container>
              <Grid item>
                <Link component={RouterLink} to="/register" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;